import React, {useEffect, useState} from "react";
import { useSelector, useDispatch } from "react-redux";
import Form from "react-bootstrap/Form";
import Modal from 'react-bootstrap/Modal';
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import SubsiteService from "../../services/subsite.service";
import UserService from "../../services/user.service";
import { getAllSubsites, allSubsites } from '../../reducers/subsite';
import { XCircle } from 'react-bootstrap-icons';

const UserSubsiteModal = (props) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subsite, setSubsite] = useState(null);
  const [userSubsites, setUserSubsites] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const subsites = useSelector(allSubsites);

  const modalClose = () => {
    props.closeModal(false);
  }

  const loadUserSubsites = async () => {
    try {
      const response = await UserService.getUserSubsites(props.user.id);
      setUserSubsites(response.data || []);
    } catch (err) {
      console.error("Error loading user subsites:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      await SubsiteService.setUserSubsite(props.user.id, subsite, sendEmail);
      setSuccess("User successfully added to subsite!");
      setSubsite(null);
      await loadUserSubsites();
    } catch (err) {
      setError(err.response?.data?.message || "Error adding user to subsite");
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleRemoveSubsite = async (subsiteId) => {
    if (!window.confirm("Are you sure you want to remove this user from this subsite?")) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      await SubsiteService.removeUserFromSubsite(props.user.id, subsiteId);
      setSuccess("User successfully removed from subsite!");
      await loadUserSubsites();
    } catch (err) {
      setError(err.response?.data?.message || "Error removing user from subsite");
    }
  };

  useEffect(() => {
    dispatch(getAllSubsites({range: [1,1000]}));
    if (props.user?.id) {
      loadUserSubsites();
    }
  },[dispatch, props.user?.id]);

  // Filter out subsites the user is already a member of
  const availableSubsites = subsites.filter(
    s => !userSubsites.some(us => us.subsiteId === s.id)
  );

  return (
    <Modal show={props.show} onHide={modalClose} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Manage User Subsites</Modal.Title>
    </Modal.Header>
    <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        <h5 className="mb-3">Current Subsites</h5>
        {userSubsites.length > 0 ? (
          <Table striped bordered hover size="sm" className="mb-4">
            <thead>
              <tr>
                <th>Subsite Name</th>
                <th>Role</th>
                <th style={{ width: '100px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {userSubsites.map(us => (
                <tr key={us.id}>
                  <td>{us.subsite?.subsiteName || 'N/A'}</td>
                  <td>
                    <Badge bg={us.role?.id === 2 ? 'primary' : 'secondary'}>
                      {us.role?.roleName || 'User'}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveSubsite(us.subsiteId)}
                      title="Remove from subsite"
                    >
                      <XCircle />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Alert variant="info">User is not currently associated with any subsites.</Alert>
        )}

        <h5 className="mb-3">Add to Subsite</h5>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="state">
            <Form.Select
              aria-label="Select subsite"
              onChange={(e) => setSubsite(e.target.value)}
              value={subsite || ""}
              disabled={availableSubsites.length === 0}
            >
              <option value="">Select Subsite</option>
              {availableSubsites.map(subsite => (
                <option key={subsite.id} value={subsite.id}>{subsite.subsiteName}</option>
              ))}
            </Form.Select>
            {availableSubsites.length === 0 && (
              <Form.Text className="text-muted">
                User is already a member of all available subsites.
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3" controlId="sendEmail">
            <Form.Check
              type="checkbox"
              label="Send email notification to user"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          </Form.Group>
          <Modal.Footer>
            <Button variant="secondary" onClick={modalClose}>
              Close
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !subsite || availableSubsites.length === 0}
            >
              {isSubmitting ? 'Adding...' : 'Add User to Subsite'}
            </Button>
          </Modal.Footer>
        </Form>
    </Modal.Body>
    </Modal>
  );
};

export default UserSubsiteModal;